import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys, useConditionTypesQuery } from '../../../shared/hooks';
import { TermSheetCondition, TermSheetConditionFormDto } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface ConditionsFormProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	condition: TermSheetCondition | null;
	close: () => void;
	isLoading: boolean;
}

export const ConditionsForm: React.FC<ConditionsFormProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	condition,
	close,
	isLoading,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const conditionTypesQuery = useConditionTypesQuery({
		enabled: condition != null,
		retry: false,
	});

	const conditionMutation = useMutation(
		async ({ values }: ConditionsFormMutationVariables) => {
			const patchValues = {
				conditionTypeId: values.conditionTypeId,
				conditionText: values.conditionText,
			} as TermSheetConditionFormDto;

			if (condition != null && condition.id == 0) {
				const { data, error } = await getApiResponseData<void>(
					api.post(
						CerebroApiEndpoints.TermSheets.Conditions(dataRoomGuid, termSheetGuid, revisionGuid),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			} else if (condition != null && condition.id > 0) {
				const { data, error } = await getApiResponseData<void>(
					api.patch(
						CerebroApiEndpoints.TermSheets.ConditionSpecific(
							dataRoomGuid,
							termSheetGuid,
							revisionGuid,
							condition?.guid
						),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Term sheet reporting was updated.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<CerebroDrawer open={condition != null} onClose={close}>
			<Formik
				initialValues={{
					conditionTypeId: condition?.conditionTypeId,
					conditionText: condition?.conditionText,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(conditionMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="conditionTypeId" required>
										Type
									</CerebroLabel>
									{conditionTypesQuery.isSuccess ? (
										<Field
											id="conditionTypeId"
											as={CerebroDropdown}
											name="conditionTypeId"
											disabled={isSubmitting}
										>
											<CerebroDropdownMenuItem key={0} value={0}>
												Select a Type
											</CerebroDropdownMenuItem>
											{conditionTypesQuery.data.map(conditionType => (
												<CerebroDropdownMenuItem
													key={conditionType.id}
													value={conditionType.id}
												>
													{conditionType.conditionTypeName}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									) : (
										<Skeleton height={60} />
									)}

									{errors.conditionTypeId && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.conditionTypeId}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="conditionText" required>
										Condition Text
									</CerebroLabel>
									<Field
										id="conditionText"
										as={CerebroTextarea}
										name="conditionText"
										rows={10}
										disabled={isSubmitting}
										error={touched.conditionText && errors.conditionText !== undefined}
									/>
									{errors.conditionText && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.conditionText}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type ConditionsFormMutationVariables = FormikUtils.MutateOnSubmitVariables<ConditionsFormValues>;
interface ConditionsFormValues {
	conditionTypeId: number | undefined;
	conditionText: string | undefined;
}
type ConditionsFormValueErrors = FormikErrors<ConditionsFormValues>;

function validateForm(values: ConditionsFormValues) {
	const errors: ConditionsFormValueErrors = {};

	if (!values.conditionTypeId || values.conditionTypeId <= 0) {
		errors.conditionTypeId = 'You must select a condition type.';
	}

	if (!values.conditionText) {
		errors.conditionText = 'You must enter condition text.';
	}

	return errors;
}
